// src/pages/admin/orders/OrderDetail.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AdminAlertBanner from '../../../components/admin/AdminAlertBanner';
import ClaimsList from '../../../components/orderer/ClaimsList';
import OrdererPhotos from '../../../components/common/OrdererPhotos';
import { claimsApi } from '../../../firebase/functionsService';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';

const functions = getFunctions(undefined, 'europe-west1');
const resolveDisputeFn   = httpsCallable(functions, 'resolveDispute');
const adminCancelFn      = httpsCallable(functions, 'adminCancelOrder');
const releaseEscrowFn    = httpsCallable(functions, 'releaseEscrow');
const refundOrdererFn    = httpsCallable(functions, 'refundOrderer');

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason]   = useState('');
  const [outcome, setOutcome] = useState('refund_orderer');
  const [driverPct, setDriverPct] = useState(50);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [loadError, setLoadError] = useState('');
  const isMobile = useIsMobile();
  const [reverseTransfer, setReverseTransfer] = useState(false);
  const [claimBusyId, setClaimBusyId] = useState(null);
  const [claimError, setClaimError] = useState('');

  useEffect(() => {
    async function fetchOrder() {
      try {
        const snap = await getDoc(doc(db, 'orders', id));
        if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
      } catch (e) {
        setLoadError('Kunne ikke laste ordre. Prøv igjen.');
      } finally {
        setLoading(false);
      }
    }
    fetchOrder();
  }, [id]);

  async function callFn(fn, data, successMsg, confirmText) {
    if (!reason.trim()) { setError('Skriv en begrunnelse'); return; }
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      await fn({ orderId: id, reason, ...data });
      setSuccess(successMsg);
      // Reload order
      const snap = await getDoc(doc(db, 'orders', id));
      if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
    } catch (e) {
      setError(e.message ?? 'Noe gikk galt');
    } finally {
      setBusy(false);
    }
  }

  async function decideClaim(claimId, decision) {
    setClaimBusyId(claimId); setClaimError('');
    try {
      await claimsApi.decideClaim({ orderId: id, claimId, decision });
    } catch (e) {
      setClaimError(e?.message || 'Kunne ikke lagre avgjørelsen. Prøv igjen.');
      setClaimBusyId(null);
      return;
    }
    // Avgjørelsen er lagret: feil ved oppfriskning er et eget problem
    try {
      const snap = await getDoc(doc(db, 'orders', id));
      if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error('OrderDetail: kunne ikke oppdatere etter avgjørelse', e);
      setClaimError('Avgjørelsen er lagret, men siden kunne ikke oppdateres. Last inn på nytt.');
    } finally {
      setClaimBusyId(null);
    }
  }

  if (loading)    return <p style={{ padding:24, color:COLORS.textLight }}>Laster ordre…</p>;
  if (loadError)  return <p style={{ padding:24, color:COLORS.dangerText }}>{loadError}</p>;
  if (!order)     return <p style={{ padding:24, color:COLORS.dangerText }}>Ordre ikke funnet</p>;

  return (
    <div style={{ padding:isMobile?16:24, maxWidth:720, flex:1, overflowY:'auto' }}>
      <button onClick={() => navigate('/admin/orders')} style={{ background:'none', border:'none', color:COLORS.blue, cursor:'pointer', fontSize:13, marginBottom:16 }}>← Tilbake</button>
      <AdminAlertBanner entityId={id} />
      <h2 style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>Ordre: {order.id.slice(0,8)}…</h2>
      <p style={{ color:COLORS.textLight, fontSize:13, marginBottom:24 }}>Status: <strong>{order.status}</strong> · PaymentIntent: {order.stripePaymentIntentId ?? '—'}</p>
      <OrdererPhotos order={order} />

      {/* Feedback */}
      {error   && <div style={{ background:COLORS.dangerBg, color:COLORS.dangerText, padding:'10px 14px', borderRadius:6, marginBottom:12, fontSize:13 }}>{error}</div>}
      {success && <div style={{ background:COLORS.successBg, color:COLORS.successText, padding:'10px 14px', borderRadius:6, marginBottom:12, fontSize:13 }}>{success}</div>}

      {/* Egne ordre hos et transportfirma håndteres av firmaet: ingen admin-handlinger */}
      {order.source === 'carrier' ? (
        <p style={{ fontSize:13, color:COLORS.textLight, background:COLORS.surfaceHover, padding:'10px 14px', borderRadius:6 }}>Dette er en egen ordre hos et transportfirma og håndteres av firmaet.</p>
      ) : (<>
      <div style={{ marginBottom:16 }}>
        <ClaimsList mode="admin" order={order} onDecide={decideClaim} busyId={claimBusyId} error={claimError} />
      </div>

      {/* Reason */}
      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:12, fontWeight:600, color:COLORS.textLight, textTransform:'uppercase', display:'block', marginBottom:4 }}>Begrunnelse (påkrevd)</label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} style={{ width:'100%', padding:8, border:`1px solid ${COLORS.border}`, borderRadius:6, fontSize:13, background:COLORS.white, color:COLORS.textDark }} />
      </div>

      {/* Actions */}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {order.status === 'disputed' && (
          <div style={{ padding:16, background:COLORS.surfaceHover, borderRadius:8 }}>
            <h3 style={{ fontSize:13, fontWeight:700, margin:'0 0 12px' }}>Løs tvist</h3>
            <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
              {[['refund_orderer','Refunder bestiller'],['pay_driver','Betal sjåfør'],['split','Custom split']].map(([v,l]) => (
                <button key={v} onClick={() => setOutcome(v)} style={{ padding:'6px 12px', borderRadius:6, border:`1px solid ${COLORS.border}`, background:outcome===v?COLORS.primary:COLORS.white, color:outcome===v?'#fff':COLORS.textDark, fontSize:13, cursor:'pointer' }}>{l}</button>
              ))}
            </div>
            {outcome === 'split' && (
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:12, color:COLORS.textLight }}>Trekkes fra bestiller og går til sjåfør (minus Tippi-gebyr): <strong>{driverPct}%</strong> · frigis til bestiller: <strong>{100-driverPct}%</strong></label>
                <input type="range" min={0} max={100} value={driverPct} onChange={e => setDriverPct(Number(e.target.value))} style={{ width:'100%', marginTop:4 }} />
              </div>
            )}
            <button onClick={() => callFn(resolveDisputeFn, { outcome, driverPct: outcome==='split' ? driverPct : undefined }, 'Tvist løst')} disabled={busy} style={btnStyle(COLORS.success, busy)}>
              {busy ? 'Behandler…' : 'Bekreft løsning'}
            </button>
          </div>
        )}

        {CANCELLABLE.includes(order.status) && (
          <div>
            <button onClick={() => callFn(adminCancelFn, {}, 'Ordre kansellert', 'Kansellere ordren? Begrunnelsen sendes til bestiller og sjåfør.')} disabled={busy} style={btnStyle(COLORS.danger, busy)}>
              {busy ? 'Behandler…' : 'Kanseller ordre'}
            </button>
            <div style={hintStyle}>Begrunnelsen sendes til bestiller og sjåfør.</div>
          </div>
        )}

        {['in_progress','delivered_pending'].includes(order.status) && (
          <button onClick={() => callFn(releaseEscrowFn, {}, 'Escrow frigjort')} disabled={busy} style={btnStyle(COLORS.blue, busy)}>
            {busy ? 'Behandler…' : 'Frigi escrow til sjåfør'}
          </button>
        )}

        {order.stripePaymentIntentId && !['refunded','cancelled'].includes(order.status) && (
          <div>
            <button
              onClick={() => callFn(refundOrdererFn, { reverseTransfer }, 'Bestiller refundert',
                reverseTransfer
                  ? 'Refundere bestiller og hente pengene tilbake fra sjåføren?'
                  : 'Refundere bestiller? Sjåføren beholder utbetalingen, og Tippi tar tapet.')}
              disabled={busy}
              style={btnStyle(COLORS.purple, busy)}
            >
              {busy ? 'Behandler…' : 'Refunder bestiller'}
            </button>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginTop:8, cursor:'pointer' }}>
              <input type="checkbox" checked={reverseTransfer} onChange={e => setReverseTransfer(e.target.checked)} />
              Hent tilbake fra sjåfør
            </label>
            <div style={hintStyle}>
              {reverseTransfer
                ? 'Er pengene overført, reverseres overføringen. Ligger de fortsatt hos Tippi, stoppes utbetalingen.'
                : 'Sjåføren beholder (eller får fortsatt) utbetalingen. Tippi tar tapet.'}
            </div>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}

// Samme statuser som adminCancelOrder godtar — etter capture må ordren refunderes i stedet
const CANCELLABLE = ['open','matched','in_progress','delivered_pending','disputed'];
const hintStyle = { fontSize:12, color:COLORS.textLight, marginTop:4 };

const btnStyle = (color, disabled) => ({
  padding:'9px 18px', background:color, color:'#fff', border:'none', borderRadius:6,
  cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.6:1, fontSize:13, fontWeight:600
});
